const MINOR_PER_MXN = 100;
const RATIO_SCALE = 1_000_000;

export function toMinorUnits(amount: number): number {
  if (!Number.isFinite(amount)) return 0;
  return Math.round(Number(amount.toFixed(2)) * MINOR_PER_MXN);
}

export function fromMinorUnits(minor: number): number {
  return minor / MINOR_PER_MXN;
}

export function normalizeMoney(amount: number): number {
  return fromMinorUnits(toMinorUnits(amount));
}

export function subtractMoney(total: number, ...deductions: number[]): number {
  const deductionMinor = deductions.reduce((sum, amount) => sum + toMinorUnits(amount), 0);
  return fromMinorUnits(Math.max(0, toMinorUnits(total) - deductionMinor));
}

export function minMoney(...amounts: number[]): number {
  return fromMinorUnits(Math.min(...amounts.map(toMinorUnits)));
}

export function clampPointsForPayment(
  points: number,
  usableAmount: number,
  payableTotal: number,
  minUsePoints = 0,
): number {
  if (points <= 0) return 0;

  const clamped = Math.max(0, minMoney(points, usableAmount, payableTotal));
  return clamped >= minUsePoints ? clamped : 0;
}

export function floorMoneyByRatio(amount: number, ratio: number): number {
  const ratioUnits = Math.round(ratio * RATIO_SCALE);
  const cappedMinor = Math.floor((toMinorUnits(amount) * ratioUnits) / RATIO_SCALE);
  return fromMinorUnits(cappedMinor);
}

export function parseMoneyInput(input: string): number | null {
  const normalized = input.trim();
  if (!/^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(normalized)) return null;

  const [whole, fraction = ''] = normalized.split('.');
  const minor = Number(whole || '0') * MINOR_PER_MXN + Number(fraction.padEnd(2, '0'));

  return Number.isSafeInteger(minor) ? fromMinorUnits(minor) : null;
}
